import * as React from 'react';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { Icon } from 'sos-common/js/components/Icon/icon';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import SpecialistkompetensDetailsSection from './specialistkompetensdetails.section';
import { SectionClass, SectionProps, SectionState, TypeOfAnsokan, FieldName } from '../PublicForm.types';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { Yrke } from '../../AnsokanForms/LegAnsokanForm.types';

export interface ISpecialistkompetensSectionProps extends SectionProps {
  showAddMoreButton: boolean;
  onChangeSpecialist: (isSpecialist: boolean, isDoubleSpecialist?: boolean) => void;
  typeOfAnsokan: TypeOfAnsokan;
  activeYrke: Yrke;
  hasWorked3YearsInEUEES: boolean;
  countryList: string[];
}

export interface ISpecialistkompetensSectionState extends SectionState {
  showDetails: boolean;
  showSecondDetails: boolean;
}

export default class SpecialistkompetensSection extends SectionClass<
  ISpecialistkompetensSectionProps,
  ISpecialistkompetensSectionState
> {
  private _detailsRef: React.RefObject<SpecialistkompetensDetailsSection>;
  private _details2Ref: React.RefObject<SpecialistkompetensDetailsSection>;

  constructor(props: ISpecialistkompetensSectionProps) {
    super(props);

    this._detailsRef = React.createRef();
    this._details2Ref = React.createRef();

    const isSpecialist = props.typeOfAnsokan === TypeOfAnsokan.Specialistkompetens;
    this.state = {
      showDetails: props.typeOfAnsokan === TypeOfAnsokan.Specialistkompetens,
      showSecondDetails: false,
      errors: this.getInitialErrorMap(isSpecialist ? [] : [FieldName.AnsokerSpecialist]),
    };
  }

  static defaultProps = {
    formLanguage: 'sv',
  } as ISpecialistkompetensSectionProps;

  public hasErrors = () => {
    // Check children
    const detailsHasErrors = this._detailsRef?.current?.hasErrors();
    const details2HasErrors = this._details2Ref?.current?.hasErrors();
    const myErrors = super.hasErrors();
    return detailsHasErrors || details2HasErrors || myErrors;
  };

  onRadioChanged = (field: FormField<string>) => {
    const errors = new Map(this.state.errors);
    const showDetails = field.value === 'Ja';

    if (showDetails) {
      errors.set(FieldName.AnsokerSpecialist, { showMessage: false, errorMessage: null });
    } else {
      errors.delete(FieldName.AnsokerSpecialist);
    }

    this.setState({ showDetails, showSecondDetails: false, errors }, () => {
      this.onChangeField(field);
    });

    this.props.onChangeSpecialist(showDetails);
  };

  toggleMoreSpecialist = (toggleValue: boolean) => {
    this.setState({ showSecondDetails: toggleValue }, () => {
      this.props.onChangeFieldValue();
    });
    this.props.onChangeSpecialist(true, toggleValue);
  };

  getPropertyNameAndBodyForYrke = (): [string, string] => {
    const { openWebAnsokanEpiTexts, activeYrke } = this.props;
    if (activeYrke === Yrke.Tandlakare)
      return ['SpecialistkompetensTandlakareBody', openWebAnsokanEpiTexts.SpecialistkompetensTandlakareBody];

    return ['SpecialistkompetensBody', openWebAnsokanEpiTexts.SpecialistkompetensBody];
  };

  public render() {
    const {
      Translate,
      showAddMoreButton,
      openWebAnsokanEpiTexts,
      isInEditMode,
      typeOfAnsokan,
      formLanguage,
      formType,
    } = this.props;
    const { showDetails, showSecondDetails } = this.state;

    const [bodyPropertyName, bodyText] = this.getPropertyNameAndBodyForYrke();

    return (
      <section>
        <EpiserverProperty propertyName="SpecialistkompetensHeading" isInEditMode={isInEditMode}>
          <h2>{openWebAnsokanEpiTexts.SpecialistkompetensHeading}</h2>
        </EpiserverProperty>

        {openWebAnsokanEpiTexts.SpecialistkompetensPreamble && (
          <EpiserverProperty
            propertyName="SpecialistkompetensPreamble"
            wrappingElement="span"
            isInEditMode={isInEditMode}>
            <p className="preamble">
              {`${openWebAnsokanEpiTexts.SpecialistkompetensPreamble} ${Translate('mandatory')}`}
            </p>
          </EpiserverProperty>
        )}

        <EpiserverProperty
          propertyName={bodyPropertyName}
          usingDangerouslySetInnerHTML={true}
          isInEditMode={isInEditMode}>
          <div dangerouslySetInnerHTML={setInnerHtml(bodyText)} />
        </EpiserverProperty>

        {typeOfAnsokan === TypeOfAnsokan.Legitimation && (
          <RadioButtonGroup
            errorMessage={this.getErrorMessage(FieldName.AnsokerSpecialist)}
            isRequired={true}
            onChange={this.onRadioChanged}
            fieldName={FieldName.AnsokerSpecialist}>
            <RadioButton value="Ja" label={Translate('ja')} />
            <RadioButton value="Nej" label={Translate('nej')} />
          </RadioButtonGroup>
        )}
        {showDetails && (
          <SpecialistkompetensDetailsSection
            ref={this._detailsRef}
            textBoxId="specialistkompetensdetailssection_1"
            onChangeFieldValue={this.props.onChangeFieldValue}
            Translate={Translate}
            formLanguage={formLanguage}
            submitButtonIsClicked={this.props.submitButtonIsClicked}
            formType={formType}
            hasWorked3YearsInEUEES={this.props.hasWorked3YearsInEUEES}
            typeOfAnsokan={typeOfAnsokan}
            countryList={this.props.countryList}
            activeYrke={this.props.activeYrke}
          />
        )}

        {showDetails && showSecondDetails && (
          <>
            <h3 className="m-t-4">{Translate('ytterligarespecialist')}</h3>
            <SpecialistkompetensDetailsSection
              ref={this._details2Ref}
              textBoxId="specialistkompetensdetailssection_2"
              onChangeFieldValue={this.props.onChangeFieldValue}
              secondSpecialist={true}
              Translate={Translate}
              formLanguage={formLanguage}
              submitButtonIsClicked={this.props.submitButtonIsClicked}
              formType={formType}
              hasWorked3YearsInEUEES={this.props.hasWorked3YearsInEUEES}
              typeOfAnsokan={typeOfAnsokan}
              countryList={this.props.countryList}
              activeYrke={this.props.activeYrke}
            />
          </>
        )}

        {showDetails && showAddMoreButton && !showSecondDetails && (
          <button
            onClick={(_) => this.toggleMoreSpecialist(true)}
            type="button"
            className="button button--white plusbutton m-t-2">
            <Icon iconType="expanded-plus" />
            {Translate('laggtillspecialist')}
          </button>
        )}
        {showDetails && showSecondDetails && (
          <button
            type="button"
            onClick={(_) => this.toggleMoreSpecialist(false)}
            className="eservice-navigation__link eservice-navigation__link--destructive stripped-button  m-t-2">
            <Icon iconType="Paperbin" />
            {Translate('tabortspecialist')}
          </button>
        )}
        <hr className="formdivider" />
      </section>
    );
  }
}
